/**
 * ui/services/profile.ts
 *
 * The player's local profile: a stable id created once per device, plus the
 * name and color picked for multiplayer. Replays and stats carry the id from
 * day one, so linking them to an account later is additive, not a migration.
 *
 * Stored under the multiplayer prefs key, so existing name/color prefs carry over.
 */
import { browserStorage } from '../../utils/browser-storage.js';

export const PROFILE_STORAGE_KEY = 'nono_player_prefs';

export interface LocalProfile {
  id: string;
  /** Empty until the player picks a name (multiplayer setup). */
  name: string;
  color: string;
  createdAt: number;
  /** Set once the profile is linked to a server account (Phase 5). */
  accountId?: string;
}

type ProfileStorage = Pick<Storage, 'getItem' | 'setItem'>;

export class ProfileService {
  static readonly instance = new ProfileService();

  /** The last profile written, so the id stays put when storage is unavailable. */
  private _lastWritten: LocalProfile | null = null;

  constructor(
    /** Defaults to localStorage, looked up on each use. */
    private readonly _storageOverride?: ProfileStorage,
    private readonly _newId: () => string = newId,
    private readonly _now: () => number = Date.now,
  ) {}

  /** The profile, creating (and saving) its id on first use. Read fresh each time (another tab may change it). */
  get(): LocalProfile {
    const stored = this._read() ?? this._lastWritten;
    const profile: LocalProfile = {
      id: typeof stored?.id === 'string' && stored.id ? stored.id : this._newId(),
      name: typeof stored?.name === 'string' ? stored.name : '',
      color: typeof stored?.color === 'string' ? stored.color : '',
      createdAt: typeof stored?.createdAt === 'number' ? stored.createdAt : this._now(),
      ...(typeof stored?.accountId === 'string' ? { accountId: stored.accountId } : {}),
    };
    if (!stored || stored.id !== profile.id || stored.createdAt !== profile.createdAt) this._write(profile);
    return profile;
  }

  /** The multiplayer name and color, once picked. */
  playerPrefs(): { name: string; color: string } | null {
    const { name, color } = this.get();
    return name && color ? { name, color } : null;
  }

  update(changes: Partial<Pick<LocalProfile, 'name' | 'color' | 'accountId'>>): LocalProfile {
    const profile = { ...this.get(), ...changes };
    this._write(profile);
    return profile;
  }

  private get _storage(): ProfileStorage | null {
    return this._storageOverride ?? browserStorage();
  }

  private _read(): Partial<LocalProfile> | null {
    try {
      const raw = this._storage?.getItem(PROFILE_STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed as Partial<LocalProfile> : null;
    } catch {
      return null;
    }
  }

  private _write(profile: LocalProfile): void {
    this._lastWritten = profile;
    try {
      this._storage?.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
    } catch {
      // Storage unavailable: the profile lasts for this page load.
    }
  }
}

/** A random id; falls back where crypto.randomUUID is missing (plain-http dev). */
export function newId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
